/**
 * Retention Policy - Phase 13
 * 
 * Configurable retention guidance and cleanup for incident/queue data.
 */

import { listQueueItems, deleteQueueItem, clearCompletedQueueItems } from '../offline/db';
import { invalidateIncidentCache } from './incidentStore';
import { INCIDENT_STATUS, SEVERITY, FOLLOWUP_STATUS } from './incidentModel';
import { computeIncidentSeverity } from './classificationRules';

// ==================== RETENTION CONFIG ====================

/**
 * Default retention periods (in milliseconds)
 */
export const DEFAULT_RETENTION = {
  // Resolved items with info severity
  RESOLVED_INFO: 7 * 24 * 60 * 60 * 1000,         // 7 days
  
  // Resolved items with warning severity
  RESOLVED_WARNING: 14 * 24 * 60 * 60 * 1000,     // 14 days
  
  // Resolved items with error severity
  RESOLVED_ERROR: 30 * 24 * 60 * 60 * 1000,       // 30 days
  
  // Critical or escalated items (retain longest)
  CRITICAL: 90 * 24 * 60 * 60 * 1000,             // 90 days
  
  // Unresolved items (never auto-cleanup)
  UNRESOLVED: Infinity,
  
  // Items with manager notes
  HAS_NOTE: 60 * 24 * 60 * 60 * 1000,             // 60 days
  
  // Needs followup items
  NEEDS_FOLLOWUP: Infinity,
  
  // Escalated items
  ESCALATED: Infinity,
  
  // Sync logs (shorter retention)
  SYNC_LOGS: 3 * 24 * 60 * 60 * 1000              // 3 days
};

/**
 * Get the retention policy configuration
 * @returns {Object}
 */
export function getIncidentRetentionPolicy() {
  return {
    ...DEFAULT_RETENTION,
    description: {
      RESOLVED_INFO: 'Resolved info-level incidents are cleaned up after 7 days',
      RESOLVED_WARNING: 'Resolved warning-level incidents are retained for 14 days',
      RESOLVED_ERROR: 'Resolved error-level incidents are retained for 30 days',
      CRITICAL: 'Critical incidents are retained for 90 days regardless of resolution',
      UNRESOLVED: 'Unresolved incidents are never automatically cleaned up',
      HAS_NOTE: 'Items with manager notes are retained for 60 days',
      NEEDS_FOLLOWUP: 'Items marked as needs follow-up are never automatically cleaned up',
      ESCALATED: 'Escalated items are never automatically cleaned up',
      SYNC_LOGS: 'Sync logs are cleaned up after 3 days'
    }
  };
}

/**
 * Determine if an incident should be retained
 * @param {Object} incident
 * @returns {{shouldRetain: boolean, reason: string}}
 */
export function shouldRetainIncident(incident) {
  const now = Date.now();
  const age = now - (incident.resolvedAt || incident.updatedAt || incident.createdAt);
  
  // Always retain unresolved items
  const unresolvedStatuses = [
    INCIDENT_STATUS.QUEUED,
    INCIDENT_STATUS.RETRYING,
    INCIDENT_STATUS.FAILED,
    INCIDENT_STATUS.CONFLICT,
    INCIDENT_STATUS.MANUAL_REVIEW
  ];
  
  if (unresolvedStatuses.includes(incident.status)) {
    return { shouldRetain: true, reason: 'Unresolved incidents are always retained' };
  }
  
  // Always retain items marked needs followup or escalated
  if (incident.followUpStatus === FOLLOWUP_STATUS.NEEDS_FOLLOWUP) {
    return { shouldRetain: true, reason: 'Needs follow-up items are always retained' };
  }
  if (incident.followUpStatus === FOLLOWUP_STATUS.ESCALATED) {
    return { shouldRetain: true, reason: 'Escalated items are always retained' };
  }
  
  // Compute severity for retention decision
  const severity = incident.severity || computeIncidentSeverity(incident);
  
  // Critical items have longest retention
  if (severity === SEVERITY.CRITICAL) {
    if (age < DEFAULT_RETENTION.CRITICAL) {
      return { shouldRetain: true, reason: 'Critical incidents retained for 90 days' };
    }
  }
  
  // Items with manager notes have extended retention
  if (incident.managerNote) {
    if (age < DEFAULT_RETENTION.HAS_NOTE) {
      return { shouldRetain: true, reason: 'Items with notes retained for 60 days' };
    }
  }
  
  // Error severity items
  if (severity === SEVERITY.ERROR) {
    if (age < DEFAULT_RETENTION.RESOLVED_ERROR) {
      return { shouldRetain: true, reason: 'Error-level incidents retained for 30 days' };
    }
  }
  
  // Warning severity items
  if (severity === SEVERITY.WARNING) {
    if (age < DEFAULT_RETENTION.RESOLVED_WARNING) {
      return { shouldRetain: true, reason: 'Warning-level incidents retained for 14 days' };
    }
  }
  
  // Info severity items (shortest retention)
  if (severity === SEVERITY.INFO) {
    if (age < DEFAULT_RETENTION.RESOLVED_INFO) {
      return { shouldRetain: true, reason: 'Info-level incidents retained for 7 days' };
    }
  }
  
  // Default: do not retain
  return { shouldRetain: false, reason: 'Retention period exceeded' };
}

/**
 * Clean up old resolved incidents based on retention policy
 * @param {Object} options - { dryRun, maxItems }
 * @returns {Promise<{deleted: number, retained: number, items: Array}>}
 */
export async function cleanupOldResolvedIncidents(options = {}) {
  const { dryRun = false, maxItems = 1000 } = options;
  
  const results = {
    deleted: 0,
    retained: 0,
    items: []
  };
  
  try {
    const queueItems = await listQueueItems();
    let processedCount = 0;
    
    for (const item of queueItems) {
      if (processedCount >= maxItems) break;
      
      // Only consider resolved/completed items for cleanup
      if (item.status !== 'completed') {
        results.retained++;
        continue;
      }
      
      const retention = shouldRetainIncident(item);
      
      if (!retention.shouldRetain) {
        results.items.push({
          id: item.id,
          type: item.type,
          createdAt: item.createdAt,
          reason: retention.reason
        });
        
        if (!dryRun) {
          await deleteQueueItem(item.id);
          results.deleted++;
        } else {
          results.deleted++; // Count as would-delete in dry run
        }
      } else {
        results.retained++;
      }
      
      processedCount++;
    }
    
    // Invalidate cache after cleanup
    if (!dryRun && results.deleted > 0) {
      invalidateIncidentCache();
    }
    
    console.log(`[RetentionPolicy] Cleanup complete:`, {
      deleted: results.deleted,
      retained: results.retained,
      dryRun
    });
    
    return results;
    
  } catch (error) {
    console.error('[RetentionPolicy] Cleanup failed:', error);
    throw error;
  }
}

/**
 * Get retention status for a specific incident
 * @param {Object} incident
 * @returns {Object}
 */
export function getRetentionStatus(incident) {
  const retention = shouldRetainIncident(incident);
  const now = Date.now();
  const age = now - (incident.resolvedAt || incident.updatedAt || incident.createdAt);
  
  // Calculate remaining retention time
  let retentionPeriod = DEFAULT_RETENTION.RESOLVED_INFO;
  const severity = incident.severity || computeIncidentSeverity(incident);
  
  if (severity === SEVERITY.CRITICAL) retentionPeriod = DEFAULT_RETENTION.CRITICAL;
  else if (severity === SEVERITY.ERROR) retentionPeriod = DEFAULT_RETENTION.RESOLVED_ERROR;
  else if (severity === SEVERITY.WARNING) retentionPeriod = DEFAULT_RETENTION.RESOLVED_WARNING;
  
  if (incident.managerNote) {
    retentionPeriod = Math.max(retentionPeriod, DEFAULT_RETENTION.HAS_NOTE);
  }
  
  const remainingMs = retentionPeriod - age;
  const remainingDays = Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
  
  return {
    shouldRetain: retention.shouldRetain,
    reason: retention.reason,
    retentionPeriodDays: Math.ceil(retentionPeriod / (24 * 60 * 60 * 1000)),
    remainingDays: Math.max(0, remainingDays),
    expiresAt: retention.shouldRetain && remainingMs > 0 ? now + remainingMs : null,
    isIndefinite: retentionPeriod === Infinity
  };
}

/**
 * Schedule automatic cleanup (returns a cleanup function to stop)
 * @param {Object} options - { intervalMs, dryRun }
 * @returns {Function} - Stop function
 */
export function scheduleAutomaticCleanup(options = {}) {
  const { intervalMs = 60 * 60 * 1000, dryRun = false } = options; // Default: hourly
  
  const runCleanup = async () => {
    try {
      console.log('[RetentionPolicy] Running scheduled cleanup...');
      await cleanupOldResolvedIncidents({ dryRun, maxItems: 100 });
    } catch (error) {
      console.error('[RetentionPolicy] Scheduled cleanup failed:', error);
    }
  };
  
  // Run immediately
  runCleanup();
  
  // Schedule recurring
  const intervalId = setInterval(runCleanup, intervalMs);
  
  // Return stop function
  return () => {
    clearInterval(intervalId);
    console.log('[RetentionPolicy] Automatic cleanup stopped');
  };
}

export default {
  getIncidentRetentionPolicy,
  shouldRetainIncident,
  cleanupOldResolvedIncidents,
  getRetentionStatus,
  scheduleAutomaticCleanup,
  DEFAULT_RETENTION
};
